import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError } from '@connectrpc/connect';
import { SpecraDataTable } from '@mono/ui-components/captains';
import { SpecraButton, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraInlineBanner, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useIsMutating } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { pricingApiConnect } from '../../../../lib/api/pricingApi.connect';
import { parseWorkbookDiff } from './usePriceVolume';
import { usePricingWrite } from './usePricingRateBook';
const T = Testids.pricingWorkspace;
const reasons = {
    unreadable: 'Unreadable or empty cell — no change applied',
    hours_unappliable: 'Hours cannot be applied — requires a nonnegative value and one unique CLIN line',
    rate_unappliable: 'Rate cannot be applied — rates must be greater than zero'
};
export function WorkbookChangesPanel({ scope, proposal, categories, periods, workbookHref, canEdit }) {
    const resolve = usePricingWrite((accept)=>pricingApiConnect.resolveProposal(scope.projectId, proposal.proposalId, accept));
    const writing = useIsMutating({
        mutationKey: [
            'pricing-write'
        ]
    }) > 0;
    const diff = parseWorkbookDiff(proposal.proposedDiffJson);
    if (proposal.status !== 'PENDING') return null;
    const rows = [
        ...(diff?.changes ?? []).map((r)=>({
                ...r,
                reason: ''
            })),
        ...(diff?.informational ?? []).map((r, i)=>({
                ...r,
                id: `info-${i}`,
                from: '',
                to: ''
            }))
    ];
    const text = (value, muted)=>/*#__PURE__*/ _jsx(SpecraText, {
            tone: muted ? 'muted' : undefined,
            children: value
        });
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": T.workbookChanges,
        "data-proposal-id": proposal.proposalId,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                title: "Workbook changes"
            }),
            resolve.error && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "danger",
                "data-testid": T.workbookChangesConflict,
                children: ConnectError.from(resolve.error).rawMessage
            }),
            !diff ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "warning",
                children: "This proposal has no field-level preview. Review it in Versions."
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraText, {
                        children: [
                            diff.document_version_number ? `Price volume v${diff.document_version_number}` : 'Price volume',
                            diff.saving_user_name ? ` saved by ${diff.saving_user_name}` : ''
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraDataTable, {
                        testId: T.workbookChangesTable,
                        rows: rows,
                        getRowId: (r)=>r.id,
                        emptyTitle: "No changes to review",
                        columns: [
                            {
                                id: 'category',
                                header: 'Category',
                                cell: (r)=>text(categories.find((c)=>c.id === r.category_id)?.title || r.category_id, !!r.reason)
                            },
                            {
                                id: 'period',
                                header: 'Period',
                                cell: (r)=>text(periods.find((p)=>p.periodKey === r.period_key)?.label || r.period_key, !!r.reason)
                            },
                            {
                                id: 'field',
                                header: 'Field',
                                cell: (r)=>text(r.field === 'hours' ? 'Hours' : 'Rate', !!r.reason)
                            },
                            {
                                id: 'from',
                                header: 'In pricing',
                                cell: (r)=>text(r.reason ? '—' : r.from === '' ? 'Missing Using' : r.from, !!r.reason)
                            },
                            {
                                id: 'to',
                                header: 'In workbook',
                                cell: (r)=>text(r.reason ? reasons[r.reason] || r.reason : r.to, !!r.reason)
                            },
                            {
                                id: 'cell',
                                header: 'Cell',
                                cell: (r)=>/*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            text(r.cell, !!r.reason),
                                            ' ',
                                            workbookHref && /*#__PURE__*/ _jsx(SpecraLink, {
                                                href: workbookHref,
                                                renderLink: ({ href, ...props })=>/*#__PURE__*/ _jsx(Link, {
                                                        ...props,
                                                        to: href
                                                    }),
                                                "data-testid": T.workbookChangesOpen,
                                                "aria-label": `Open workbook at ${r.cell}`,
                                                children: "open"
                                            })
                                        ]
                                    })
                            }
                        ]
                    })
                ]
            }),
            canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": T.workbookChangesAccept,
                        disabled: writing || resolve.isPending || !diff?.changes.length,
                        onClick: ()=>resolve.mutate(true),
                        children: resolve.isPending && resolve.variables === true ? 'Accepting…' : 'Accept all'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": T.workbookChangesReject,
                        disabled: writing || resolve.isPending,
                        onClick: ()=>resolve.mutate(false),
                        children: resolve.isPending && resolve.variables === false ? 'Rejecting…' : 'Reject all'
                    })
                ]
            })
        ]
    });
}
